/**
 * IAN Assist — Agent Client
 * POSTs RequestV1 to stateless n8n webhook, returns ResponseV1.
 *
 * Security model:
 *   - n8n is STATELESS: receives email, processes, returns cards, discards.
 *   - Auth via AAD JWT in Authorization header.
 *   - All LLM API keys live in n8n, never in the client.
 *   - No data persisted server-side.
 */

import { getAccessToken, getUserProfile } from './officeAuth';
import { buildRequest, parseResponse } from '../shared/schema';

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;

/**
 * Call the n8n email-agent webhook.
 *
 * @param {Object} params
 * @param {string} params.n8nUrl - Base webhook URL
 * @param {Object} params.email - Email data from emailReader
 * @param {Object} params.config - User config from roamingSettings
 * @param {string} params.persona - Active persona key
 * @param {string} params.trigger - 'mail_opened' | 'manual' | 'compose'
 * @param {Object|null} params.selection - Selected text, if any
 * @param {AbortSignal} [params.signal] - AbortController signal for cancellation
 * @returns {Promise<Object>} Parsed ResponseV1
 */
export async function callAgent({ n8nUrl, email, config, persona, trigger, selection = null, workflowId = '', customDag = null, signal }) {
  console.group('[agentClient] callAgent');
  console.log('[agentClient] URL:', n8nUrl);
  console.log('[agentClient] persona:', persona, '| trigger:', trigger, '| workflowId:', workflowId);
  console.log('[agentClient] enabledIntents:', config?.enabledIntents);
  console.log('[agentClient] email subject:', email?.subject || '(none)');

  let token;
  try {
    token = await getAccessToken();
    console.log('[agentClient] auth token:', token ? `present (${token.length} chars)` : 'null (no SSO)');
  } catch (authErr) {
    console.error('[agentClient] getAccessToken() threw:', authErr.message);
    token = null;
  }

  const profile = getUserProfile();
  console.log('[agentClient] user:', profile.email || '(unknown)');

  const requestBody = buildRequest({
    tenantId: 'xceedance',
    userId: profile.email,
    userDisplayName: profile.displayName,
    persona,
    trigger,
    email,
    selection,
    config,
    workflowId,
    customDag,
  });

  console.log('[agentClient] request body keys:', Object.keys(requestBody));
  console.log('[agentClient] request.config.enabledIntents:', requestBody.config?.enabledIntents);
  console.log('[agentClient] request.config.targetLanguage:', requestBody.config?.targetLanguage);

  const startTime = Date.now();
  let lastError = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      console.log(`[agentClient] fetch attempt ${attempt + 1}/${MAX_RETRIES + 1} → POST ${n8nUrl}`);
      console.log('[agentClient] request headers:', Object.keys(headers));
      console.log('[agentClient] request body size:', JSON.stringify(requestBody).length, 'bytes');

      const response = await fetch(n8nUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
        signal,
      });

      console.log(`[agentClient] response: ${response.status} ${response.statusText}`);
      console.log('[agentClient] response headers:', [...response.headers.entries()].map(([k, v]) => `${k}: ${v}`).join(', '));

      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        console.error(`[agentClient] HTTP ${response.status} error body:`, errorText.substring(0, 500));

        if (response.status === 401 || response.status === 403) {
          console.groupEnd();
          throw new Error(`Authentication failed (${response.status}). Please sign in again.`);
        }

        if ((response.status === 429 || response.status >= 500) && attempt < MAX_RETRIES) {
          lastError = new Error(`Server error ${response.status}: ${errorText}`);
          console.warn(`[agentClient] retryable error, waiting ${RETRY_DELAY_MS * Math.pow(2, attempt)}ms`);
          await sleep(RETRY_DELAY_MS * Math.pow(2, attempt));
          continue;
        }

        console.groupEnd();
        throw new Error(`Request failed (${response.status}): ${errorText}`);
      }

      // Read body as text first to handle empty/truncated responses gracefully
      const responseText = await response.text();
      console.log('[agentClient] response body length:', responseText.length, 'chars');

      if (!responseText || !responseText.trim()) {
        throw new Error('Empty response from server. The n8n workflow may have timed out or returned no data.');
      }

      let rawJson;
      try {
        rawJson = JSON.parse(responseText);
      } catch (parseErr) {
        console.error('[agentClient] JSON parse failed:', parseErr.message);
        console.error('[agentClient] response body preview:', responseText.substring(0, 500));
        throw new Error('Invalid response from server (not valid JSON). The n8n workflow may have returned an error or truncated response.');
      }

      console.log('[agentClient] response cards:', rawJson.cards?.length || 0);
      console.log('[agentClient] response intent:', rawJson.intent);
      console.log('[agentClient] response latency:', rawJson.telemetry?.latencyMs, 'ms (server)');

      const parsed = parseResponse(rawJson);
      parsed.telemetry.clientLatencyMs = Date.now() - startTime;
      console.log('[agentClient] total client latency:', parsed.telemetry.clientLatencyMs, 'ms');
      console.groupEnd();

      return parsed;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.warn('[agentClient] request aborted');
        console.groupEnd();
        throw error;
      }

      console.error(`[agentClient] attempt ${attempt + 1} failed:`, error.name, error.message);
      if (error.stack) console.error('[agentClient] stack:', error.stack.split('\n').slice(0, 3).join('\n'));
      lastError = error;

      if (attempt < MAX_RETRIES && isRetryable(error)) {
        console.warn(`[agentClient] retrying in ${RETRY_DELAY_MS * Math.pow(2, attempt)}ms (${error.name}: ${error.message})`);
        await sleep(RETRY_DELAY_MS * Math.pow(2, attempt));
        continue;
      }

      console.groupEnd();
      throw error;
    }
  }

  console.error('[agentClient] all retries exhausted');
  console.groupEnd();
  throw lastError || new Error('Agent call failed after retries');
}

function isRetryable(error) {
  return (
    error.name === 'TypeError' || // Network errors
    error.message?.includes('fetch') ||
    error.message?.includes('network') ||
    error.message?.includes('Server error')
  );
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
